import { Component, inject, signal, computed, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Wallet, Lock, RefreshCw, ArrowLeftRight } from 'lucide-angular';
import { RouterLink } from '@angular/router';
import { CashRegisterService } from '../../../core/services/cash-register.service';
import { TranslationService } from '../../../core/services/translation.service';
import { CashSessionSummary } from '../../../core/models/cash-register.model';

@Component({
    selector: 'app-cash-session-bar',
    standalone: true,
    imports: [CommonModule, LucideAngularModule, RouterLink],
    template: `
        @if (session()) {
            <div class="fixed top-14 left-0 right-0 z-30 flex items-center justify-between px-4 py-1.5 text-xs font-medium transition-colors"
                 [ngClass]="isDark ? 'bg-emerald-900/40 text-emerald-300 border-b border-emerald-800/50' : 'bg-emerald-50 text-emerald-800 border-b border-emerald-200'">
                <div class="flex items-center gap-2 min-w-0 flex-1">
                    <lucide-icon [img]="Wallet" [size]="14" class="flex-none"></lucide-icon>
                    <span class="truncate flex-1 min-w-0">{{ session()!.cashierName }} <span class="opacity-60">|</span> {{ getRegisterName() }}</span>
                </div>
                <div class="flex items-center gap-3 flex-none">
                    <span class="font-semibold">
                        {{ t().cash_session.balance || 'Solde' }}: {{ formatAmount(summary()?.expectedCash) }} {{ session()!.currency }}
                    </span>
                    <a routerLink="/cash-transfer"
                       class="relative opacity-70 hover:opacity-100 transition-opacity"
                       [title]="t().cash_transfer.title || 'Transfert'">
                        <lucide-icon [img]="ArrowLeftRight" [size]="14"></lucide-icon>
                        @if (pendingCount() > 0) {
                            <span class="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-amber-500 text-white text-[8px] font-bold flex items-center justify-center">
                                {{ pendingCount() }}
                            </span>
                        }
                    </a>
                    <button (click)="refreshSummary()" class="opacity-60 hover:opacity-100 transition-opacity" [title]="t().cash_session.refresh || 'Actualiser'">
                        <lucide-icon [img]="RefreshCw" [size]="12"></lucide-icon>
                    </button>
                </div>
            </div>
        }
    `
})
export class CashSessionBarComponent implements OnInit, OnDestroy {
    private cashService = inject(CashRegisterService);
    private translationService = inject(TranslationService);
    t = this.translationService.t;

    readonly Wallet = Wallet;
    readonly Lock = Lock;
    readonly RefreshCw = RefreshCw;
    readonly ArrowLeftRight = ArrowLeftRight;

    /** Fourni par le parent (main-layout.component.html) — reactif au meme
     * signal darkMode() que le reste de l'app, plutot qu'un snapshot DOM pris
     * une seule fois au montage. */
    @Input() isDark = false;

    readonly session = this.cashService.activeSession;
    readonly summary = signal<CashSessionSummary | null>(null);
    readonly pendingCount = signal(0);
    private refreshInterval: ReturnType<typeof setInterval> | null = null;

    ngOnInit() {
        this.loadSummary();
        this.loadPendingCount();
        // Refresh summary + pending count every 60s
        this.refreshInterval = setInterval(() => {
            this.loadSummary();
            this.loadPendingCount();
        }, 60_000);
    }

    ngOnDestroy() {
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    }

    getRegisterName(): string {
        const session = this.session();
        if (!session) return '';
        const registers = this.cashService.registers();
        const reg = registers.find(r => r.id === session.cashRegisterId);
        return reg?.name || `#${session.cashRegisterId}`;
    }

    formatAmount(value: string | undefined | null): string {
        if (!value) return '0.00';
        return Number(value).toFixed(2);
    }

    async loadSummary() {
        const session = this.session();
        if (!session) return;
        try {
            const s = await this.cashService.getSessionSummary(session.id);
            this.summary.set(s);
        } catch {
            // Silent — bar is informational
        }
    }

    async refreshSummary() {
        await this.loadSummary();
        await this.loadPendingCount();
    }

    private async loadPendingCount() {
        const session = this.session();
        if (!session) return;
        try {
            const transfers = await this.cashService.getPendingTransfers();
            this.pendingCount.set(transfers.length);
        } catch {
            // Silent
        }
    }
}
